import React, { useEffect, useRef, useState } from "react";
import FormDropDown from "../../../forms/DropDownList/FormDropDown";
import "./ExpeditionControls.scss";
import { convertMSToHMS } from "../../../../helpers/Dates";

const ALERT_TYPES = {
    MESSAGE_ALERT: 'message_alert',
    ITEM_COLLECTED_ALERT: 'item_collected_alert',
    ITEM_USED_ALERT: 'item_used_alert',
    WAYPOINT_REACHED_ALERT: 'waypoint_reached_alert',
    CHECKPOINT_REACHED_ALERT: 'checkpoint_reached_alert',
    CHAT_UNLOCKED_ALERT: 'chat_unlocked_alert',
    COMPASS_FROZEN_ALERT: 'compass_frozen_alert',
    CHALLENGE_ALERT: 'challenge_alert',
};

const ExpeditionActiveOverlays = (props) => {
    const [tick, setTick] = useState(0);
    const [buttonClickedWaitingForUpdate, setButtonClickedWaitingForUpdate] = useState(false);
    useEffect(() => {
        setButtonClickedWaitingForUpdate(false);
    }, [props]);

    useEffect(() => {
        const interval = setInterval(() => {
            setTick((prevTick) => prevTick + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const getOverlaysStateData = () => {
        let overlaysStateData = null;
        if (props.previewControls && props?.passedInPreviewData?.activeOverlays) {
            overlaysStateData = props.passedInPreviewData.activeOverlays;
        }
        if (overlaysStateData === null) {
            overlaysStateData = props?.featureVars?.activeOverlays;
        }
        if (overlaysStateData) {
            return overlaysStateData;
        }
        return {
            alerts: [],
            teamStats: [],
        };
    }

    const getTeamData = (teamIndex) => {
        return props.customisationData?.setup?.teams?.teams?.[teamIndex] ?? ({name: "Team " + (teamIndex + 1)});
    }

    const getItemData = (itemIndex) => {
        return props.customisationData?.setup?.inventory?.items?.[itemIndex] ?? ({name: "Item " + (itemIndex + 1)});
    }

    const getChallengeData = (challengeIndex) => {
        return props.customisationData?.setup?.challenges?.challenges?.[challengeIndex] ?? ({name: "Challenge " + (challengeIndex + 1)});
    }

    const getDisplayFormattedTime = (timeInMS) => {
        if (isNaN(timeInMS) || timeInMS <= 0) return "0:00:00";
        const timeObj = convertMSToHMS(timeInMS, true);
        return `${timeObj.h}:${timeObj.m_str}:${timeObj.s_str}`;
    }

    const overlaysStateData = getOverlaysStateData();
    const featureIdForCommands = props.previewControls ? 'previewFrame' : props.featureId;

    return (
        <>
        {(!props.featureReady || !props.chatConnected) && 
            <div className="fl-column">
                <div className="bold-text warning-text">
                    Your feature needs to be loaded and ready and chat must be connected in order to see the active alerts list.
                </div>
            </div>
        }
        {props.featureReady && props.chatConnected && 
        <>
            <div className="fl-column" style={{opacity: props.featureVars.expeditionActiveOverlays.alertsVisible === false ? 0.5 : 1}}>
                <h4>Active Alerts</h4>
                {overlaysStateData.alerts && overlaysStateData.alerts.length > 0 && overlaysStateData.alerts.map((alert, index) => {
                    return (
                        <div key={index} className="quest-active-overlay normal-body-text-colour">
                            <div className="fl-row grow">
                                {alert.type === ALERT_TYPES.MESSAGE_ALERT &&
                                    <div className="fl-column no-gap">
                                        <div className="var-label">Message for {getTeamData(alert.teamIndex).name}</div>
                                        <div className="grow">{alert.message}</div>
                                    </div> 
                                }
                                {alert.type === ALERT_TYPES.ITEM_COLLECTED_ALERT &&
                                    <div className="fl-column no-gap">
                                        <div className="var-label">Item Collected by {getTeamData(alert.teamIndex).name}</div>
                                        <div className="grow">{getItemData(alert.itemIndex).name}</div>
                                    </div> 
                                }
                                {alert.type === ALERT_TYPES.ITEM_USED_ALERT &&
                                    <div className="fl-column no-gap">
                                        <div className="var-label">Item Used by {getTeamData(alert.teamIndex).name}</div>
                                        <div className="grow">{getItemData(alert.itemIndex).name}</div>
                                    </div>
                                }
                                {alert.type === ALERT_TYPES.WAYPOINT_REACHED_ALERT &&
                                    <div className="fl-column no-gap">
                                        <div className="var-label">Waypoint Reached by {getTeamData(alert.teamIndex).name}</div>
                                    </div>
                                }
                                {alert.type === ALERT_TYPES.CHECKPOINT_REACHED_ALERT &&
                                    <div className="fl-column no-gap">
                                        <div className="var-label">Checkpoint Reached by {getTeamData(alert.teamIndex).name}</div>
                                    </div>
                                }
                                {alert.type === ALERT_TYPES.CHAT_UNLOCKED_ALERT &&
                                    <div className="fl-column no-gap">
                                        <div className="var-label">Chat Unlocked for {alert.allTeams ? 'All Teams' : getTeamData(alert.teamIndex).name}</div>
                                        <div className="grow">{getDisplayFormattedTime((alert.startTime + alert.unlockedTime) * 1000 - Date.now())}</div>
                                    </div>
                                }
                                {alert.type === ALERT_TYPES.COMPASS_FROZEN_ALERT &&
                                    <div className="fl-column no-gap">
                                        <div className="var-label">Compass Frozen for {getTeamData(alert.teamIndex).name}</div>
                                        <div className="grow">{getDisplayFormattedTime((alert.startTime + alert.lockedTime) * 1000 - Date.now())}</div>
                                    </div>
                                }
                                {alert.type === ALERT_TYPES.CHALLENGE_ALERT &&
                                    <div className="fl-column no-gap">
                                        <div className="var-label">{getTeamData(alert.teamIndex).name}: {getChallengeData(alert.challengeIndex).name}</div>
                                        {alert.completed && <div className="grow">Challenge Completed{getChallengeData(alert.challengeIndex).pointsChallenge && getChallengeData(alert.challengeIndex).xp > 0 && <>: +{getChallengeData(alert.challengeIndex).xp}xp</>}</div>}
                                        {!alert.completed && <div className="grow">Challenge In Progress{getChallengeData(alert.challengeIndex).timedChallenge && <>: {getDisplayFormattedTime((alert.startTime + getChallengeData(alert.challengeIndex).time) * 1000 - Date.now())}</>}</div>}
                                    </div>
                                }
                                <div className="grow"></div>
                                <div className="shrink">
                                    <button className={`quest-small-control-btn ${!buttonClickedWaitingForUpdate ? 'red' : 'grey'}`} disabled={buttonClickedWaitingForUpdate} 
                                        onClick={
                                            () => {
                                                // Close the alert
                                                setButtonClickedWaitingForUpdate(true);
                                                props.sendFeatureCommandFunc(null, "CLOSE_ALERT", featureIdForCommands, alert.id);
                                            }
                                        }
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {(!overlaysStateData.alerts || overlaysStateData.alerts.length === 0) && 
                    <div className="light-text">No Active Alerts</div>
                }
            </div>

            <hr />

            <div className="fl-column" style={{opacity: props.featureVars.expeditionActiveOverlays.teamBannersVisible === false ? 0.5 : 1}}>
                <h4>Active Team Banners</h4>
                {overlaysStateData.teamStats && overlaysStateData.teamStats.length > 0 && overlaysStateData.teamStats.map((teamStat, index) => {
                    const teamData = getTeamData(teamStat.teamIndex);
                    return (
                        <div key={index} className="quest-active-overlay">
                            <div className="fl-column">
                                <div className="fl-row grow"><div className="var-label">{teamData.name} {teamStat.isHero ? <>Hero Panel</> : <>Status Panel</>}</div></div>
                            </div>
                            <div className="grow"></div>
                            <div className="shrink">
                                <button className={`quest-small-control-btn ${!buttonClickedWaitingForUpdate ? 'red' : 'grey'}`} disabled={buttonClickedWaitingForUpdate} 
                                    onClick={
                                        () => {
                                            // Close the alert
                                            setButtonClickedWaitingForUpdate(true);
                                            props.sendFeatureCommandFunc(null, teamStat.isHero ? "HIDE_TEAM_HERO_PANEL" : "HIDE_TEAM_STATS", featureIdForCommands, teamStat.isHero ? { teamIndex: teamStat.teamIndex } : teamStat.teamIndex);
                                        }
                                    }
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    );
                })}
                {(!overlaysStateData.teamStats || overlaysStateData.teamStats.length === 0) && 
                    <div className="light-text">No Active Team Banners</div>
                }
            </div>
        </>
        }
        </>
    );
}

export default ExpeditionActiveOverlays;
